import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders, HttpErrorResponse } from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { environment } from '../../../../../environments/environment';
import { GeneralResponse } from '../../shared/models/general-response.model';
import { ManageActivitiesModel } from '../../manage-activities/models/manage-activities.model';
import { companiesModel } from '../../company-management/models/companies.model';
import { contactsModel } from '../../contact-management/models/contacts.model';
import { opportunitiesModel } from '../../opportunities/models/opportunities.model';
import { opportunitiesStatusModel } from '../../opportunities/models/opportunity-status.model';
import { OpportunityCompanyModel } from '../../opportunity-dashboard/models/opportunity-company.model';
import { customAttributesModel } from '../../company-management/models/customAttributes.model';
import { ProductFormModels } from '../../product-form/models/product-form-model';
import { countriesModel } from '../../company-management/models/countries.model';
import { UserLocalizationService } from '../../shared/services/UserLocalization.service';
import { FieldApiMetadata, ExecuteFieldApiRequest, ExecuteFieldApiResponse } from '../models/field-api-config.model';

export interface OtpRequestData {
  token: string;
  validForInMilliSec: number;
  resendOtpIntervalInSeconds: number;
  message: string;
}

export interface OtpValidateData {
  roleId: number;
  status: string;
  message: string;
  authenticated: boolean;
  projectId: number,
  publicFormActivityModuleType: number,
  publicFormActivityName: string,
  triggerActivitySerialNumber: number;
  triggerSerialNumberDisplayAttributeId: number;
  alreadySubmittedActivityId: number,
  submitterUserId: number,
  approvalRemarks: string,
  serverUrl: string
}

@Injectable({
  providedIn: 'root'
})
export class PublicFormService {

  // Base URL
  private baseUrl = environment.publicFormUrl;

  constructor(private http: HttpClient, private userLocalization:UserLocalizationService) { }

  /**
   * Request OTP for public form access
   * @param uniqueKey The unique form key from the URL
   * @returns Observable with OTP request response including token
   */
  requestOTP(uniqueKey: string): Observable<GeneralResponse<OtpRequestData>> {
    const headers = new HttpHeaders({
      'X-Form-Key': uniqueKey
    });

    return this.http.post<GeneralResponse<OtpRequestData>>(
      this.baseUrl + `/loyalty/api/public-form/request-otp`,{},{ headers: headers }
    ).pipe(
      catchError(this.handleError)
    );
  }

  /**
   * Validate OTP for public form access
   * @param uniqueKey The unique form key from the URL
   * @param token The token received from requestOTP
   * @param otp The OTP entered by the user
   * @returns Observable with OTP validation response
   */
  validateOTP(uniqueKey: string, token: string, otp: string): Observable<GeneralResponse<OtpValidateData>> {
    const headers = new HttpHeaders({
      'X-Form-Key': uniqueKey
    });

    return this.http.post<GeneralResponse<OtpValidateData>>(
      this.baseUrl + `/loyalty/api/public-form/validate-otp`,
      {
        token: token,
        otp: parseInt(otp, 10)
      },
      { headers }
    ).pipe(
      catchError(this.handleError)
    );
  }

  setBaseUrl(serverUrl: string) {
    this.baseUrl = serverUrl;
  }

  /**
   * Handle HTTP errors
   * @param error The HTTP error response
   * @returns Observable with error
   */
  private handleError(error: HttpErrorResponse): Observable<never> {
    let errorMessage = 'An error occurred: ' + error.message;

    if (error.error instanceof ErrorEvent) {
      // Client-side error
      errorMessage = error.error.message;
    } else {
      // Server-side error
      // Check if error has the GeneralResponse structure
      if (error.error && typeof error.error === 'object') {
        if (error.error.message) {
          errorMessage = error.error.message;
        } else if (error.error.responseData && typeof error.error.responseData === 'string') {
          errorMessage = error.error.responseData;
        }
      } else if (error.status === 0) {
        errorMessage = 'Unable to connect to the server. Please check your internet connection.';
      } else {
        errorMessage = `Server error: ${error.statusText || 'Unknown error'}`;
      }
    }

    console.error('PublicFormService Error:', error);

    // Return error in GeneralResponse format
    const errorResponse: GeneralResponse<any> = {
      statusCode: error.status || 500,
      message: errorMessage,
      responseData: null
    };

    return throwError(errorResponse);
  }

  /**
   * Request OTP for public form access
   * @param uniqueKey The unique form key from the URL
   * @returns Observable with OTP request response including token
   */
  getAllActivities(projectId: number, uniqueKey: string) {

    const headers = new HttpHeaders({
      'X-Form-Key': uniqueKey
    });

    return this.http.get<ManageActivitiesModel[]>(
      this.baseUrl + '/transactions/api/public-form/manageActivities/?projectId=' + projectId, { headers });
  }

  getFieldVisibilitySettings(projectId: number, moduleType: number, roleId: number, uniqueKey: string) {

    const headers = new HttpHeaders({
      'X-Form-Key': uniqueKey
    });
    return this.http.get(
      this.baseUrl + '/setting/api/public-form/getFieldVisibilitySettings?projectId=' + projectId + '&moduleType=' + moduleType + '&roleId=' + roleId, { headers });
  }

  getLinkedDataListDetails(moduleType, projectId: number, uniqueKey: string) {
    const headers = new HttpHeaders({
      'X-Form-Key': uniqueKey
    });
    return this.http.get( this.baseUrl + '/transactions/api/public-form/getLinkedDataList?projectId=' + projectId + '&moduleType=' + moduleType, { headers })
  }

  getFields(moduleType, projectId: number, uniqueKey: string) {
    const headers = new HttpHeaders({
      'X-Form-Key': uniqueKey
    });
    return this.http.get<companiesModel>( this.baseUrl + '/transactions/api/public-form/getFields?projectId=' + projectId + '&moduleType=' + moduleType, { headers })
  }

  getCustomerContact(projectId: number, companyId: string | number, uniqueKey: string) {
    const headers = new HttpHeaders({
      'X-Form-Key': uniqueKey
    });
    return this.http.get<contactsModel[]>(
       this.baseUrl + '/transactions/api/public-form/getCustomerContacts?projectId=' + projectId + '&companyId=' + companyId, { headers });

  }

  getAllOpportunityDetails2(companyId, teamFlag: boolean = false, projectFlag: boolean = false, selectedProject, uniqueKey: string) {
    const headers = new HttpHeaders({
      'X-Form-Key': uniqueKey
    });
    return this.http.get<opportunitiesModel[]>(
       this.baseUrl + '/transactions/api/public-form/getOpportunityLookup?projectId=' + selectedProject + '&companyId=' + companyId + "&teamFlag=" + teamFlag + "&projectFlag=" + projectFlag, { headers })
  }

  getCompanyIdName(projectId: number, moduleType: Number, customerView: number = 3, uniqueKey: string) {
    const headers = new HttpHeaders({
      'X-Form-Key': uniqueKey
    });
    return this.http.get<companiesModel[]>(
       this.baseUrl + '/transactions/api/public-form/getCompanyIdName?projectId=' + projectId + '&moduleType=' + moduleType + '&customerView=' + customerView, { headers });
  }
  getOpportunity(opportunityId, projectId: number, uniqueKey: string) {
    const headers = new HttpHeaders({
      'X-Form-Key': uniqueKey
    });
    return this.http.get<opportunitiesStatusModel[]>(
       this.baseUrl + '/transactions/api/public-form/getOpportunity?projectId=' + projectId + '&opportunityId=' + opportunityId, { headers });
  }
  getFieldRelation(attributeId: number, lookupvalueId: string, uniqueKey: string) {
    const headers = new HttpHeaders({
      'X-Form-Key': uniqueKey
    });
    return this.http.get( this.baseUrl + '/setting/api/public-form/getChildAttributes?attributeId=' + attributeId + '&attributeLookupId=' + lookupvalueId, { headers });
  }

  getCompanyDetails(companyId, projectId, uniqueKey: string) {
    const headers = new HttpHeaders({
      'X-Form-Key': uniqueKey
    });
    return this.http.get<companiesModel[]>(
       this.baseUrl + '/transactions/api/public-form/getCompany?projectId=' + projectId + '&companyId=' + companyId, { headers });
  }

  getOpportunityCompanies(projectId: number, opportunityId: number, uniqueKey: string) {
    const headers = new HttpHeaders({
      'X-Form-Key': uniqueKey
    });
    return this.http.get<OpportunityCompanyModel[]>(
       this.baseUrl + '/reportsfetcher/api/public-form/dashboard/getCompanies?projectId=' + projectId + '&opportunityId=' + opportunityId, { headers }
    );
  }

  getActivityLookup(moduleType, projectId: number, companyId, opportunityId, uniqueKey: string) {
    const headers = new HttpHeaders({
      'X-Form-Key': uniqueKey
    });

    if (opportunityId != null) {

      return this.http.get<customAttributesModel[]>(
         this.baseUrl + '/transactions/api/public-form/getActivityLookup?projectId=' + projectId + '&moduleType=' + moduleType + '&companyId=' + companyId + '&opportunityId=' + opportunityId, { headers })

    } else {

      return this.http.get<customAttributesModel[]>(
         this.baseUrl + '/transactions/api/public-form/getActivityLookup?projectId=' + projectId + '&moduleType=' + moduleType + '&companyId=' + companyId, { headers })
    }
  }

  getProductLookup(projectId: number, uniqueKey: string, advanceFilters: any[] = []) {
    const headers = new HttpHeaders({
      'X-Form-Key': uniqueKey
    });
    return this.http.post(
       this.baseUrl + '/transactions/api/public-form/getProductLookup?projectId=' + projectId, advanceFilters, { headers });
  }

  getSelectedProductForm(productFormId, uniqueKey: string) {
    const headers = new HttpHeaders({
      'X-Form-Key': uniqueKey
    });
    return this.http.get<ProductFormModels[]>(
       this.baseUrl + '/setting/api/public-form/productForm/getSetting/' + productFormId, { headers });
  }

  validateExpression(addAttributeData, uniqueKey: string) {
    const headers = new HttpHeaders({
      'X-Form-Key': uniqueKey
    });
    return this.http.post<any[]>(
       this.baseUrl + '/setting/api/public-form/calculatedField/validate', addAttributeData, { headers });
  }

  uploadImage(moduleType: Number, image: File, projectId: number, uniqueKey: string): Observable<any> {
    const formData: any = new FormData();
    formData.append("projectId", projectId);
    formData.append("moduleType", moduleType);
    formData.append("image", image);

    const headers = new HttpHeaders({
      'X-Form-Key': uniqueKey
    });

    return this.http.post(
       this.baseUrl + '/transactions/api/public-form/uploadFieldImage', formData, { headers }).pipe(
      catchError(this.errorMgmt)
    )
  }

  errorMgmt(error: HttpErrorResponse) {
    let errorMessage = '';
    if (error.error instanceof ErrorEvent) {
      // Get client-side error
      errorMessage = error.error.message;
    } else {
      // Get server-side error
      errorMessage = `Error Code: ${error.status}\nMessage: ${error.message}`;
    }
    return throwError(errorMessage);
  }

  uploadDoc(moduleType: Number, mediaFile: File, projectId: number, uniqueKey: string): Observable<any> {
    const formData: any = new FormData();
    formData.append("projectId", projectId);
    formData.append("moduleId", moduleType);
    formData.append("media", mediaFile);

    const headers = new HttpHeaders({
      'X-Form-Key': uniqueKey
    });

    return this.http.post(
       this.baseUrl + '/transactions/api/public-form/uploadAttributeRefDoc', formData, { headers });
  }

  getAllCountries(projectId: number, uniqueKey: string){

    const headers = new HttpHeaders({
      'X-Form-Key': uniqueKey
    });
      return this.http.get<countriesModel[]>(
         this.baseUrl + '/setting/api/public-form/getAllCountries?projectId='+projectId, {headers});
    }

    getParticularCompany(id,moduleType, projectId: number, uniqueKey: string){

      const headers = new HttpHeaders({
      'X-Form-Key': uniqueKey
    });

    if(moduleType==1){
      return this.http.get<companiesModel[]>(
        this.baseUrl + '/transactions/api/contact/getContact?projectId='+projectId+'&contactId='+id);
    }
    if(moduleType==2){
        return this.http.get<companiesModel[]>(
         this.baseUrl + '/transactions/api/public-form/getCompany?projectId='+projectId+'&companyId='+id, {headers});
    
    }
    if(moduleType==3){
      return this.http.get<companiesModel[]>(
       this.baseUrl + '/transactions/api/public-form/getOpportunity?projectId='+projectId+'&opportunityId='+id, {headers});
      
    }
    if(moduleType==4){
      return this.http.get<companiesModel[]>(
        this.baseUrl + '/transactions/api/product/getProduct?projectId='+projectId+'&productId='+id);
      
    }
    else{
      return this.http.get<companiesModel[]>(
       this.baseUrl + '/transactions/api/public-form/getActivity?projectId='+projectId+'&activityId='+id, {headers});
      
    }
  }

  getAllContactDetailsForSelfAndTeam(projectId: number,moduleType:Number, uniqueKey: string){ 

    const headers = new HttpHeaders({
      'X-Form-Key': uniqueKey
    });
    return this.http.get<contactsModel[]>(
     this.baseUrl + '/transactions/api/public-form/getContactLookup?projectId='+ projectId+'&moduleType='+moduleType, {headers});

  }

  add(addData, moduleType, uniqueKey: string, publicFormSubmitterEmail?: string){

    const headers = new HttpHeaders({
      'X-Form-Key': uniqueKey
    });
    addData.transTimeZone=this.userLocalization.getUserTimeZoneCode();
    
    // Build query parameter if email is provided
    const emailParam = publicFormSubmitterEmail ? `?publicFormSubmitterEmail=${encodeURIComponent(publicFormSubmitterEmail)}` : '';
    
    switch (moduleType){
      case 1:{        
        return this.http.post(this.baseUrl + '/transactions/api/contact/addEditContact' + emailParam, addData);
        //break;
      }
      case 2:{
        return this.http.post(this.baseUrl + '/transactions/api/company/addEditCompany' + emailParam, addData);
        //break;
      }
      case 3:{
        return this.http.post(this.baseUrl + '/transactions/api/opportunity/addEditOpportunity' + emailParam, addData);
        //break;
      }
      case 4:{
        addData.userTime=this.userLocalization.getUserTime();
        return this.http.post(this.baseUrl + '/transactions/api/product/addEditProduct' + emailParam, addData);
        //break;
      }
      default: { 
        return this.http.post( this.baseUrl + '/transactions/api/public-form/addEditActivity' + emailParam, addData, {headers});
        //break;
     } 
    }

  }

  /**
   * Get field API metadata for public form
   * Returns minimal metadata (fieldId, requiredFieldIds, buttonConfig) for rendering
   * @param projectId Project ID
   * @param moduleType Module type
   * @param uniqueKey The unique form key from the URL
   * @returns Observable with field API metadata
   */
  getFieldApiMetadata(projectId: number, moduleType: number, uniqueKey: string): Observable<GeneralResponse<FieldApiMetadata[]>> {
    const headers = new HttpHeaders({
      'X-Form-Key': uniqueKey
    });

    return this.http.get<GeneralResponse<FieldApiMetadata[]>>(
      this.baseUrl + `/loyalty/api/public-form/field-api-metadata?projectId=${projectId}&moduleType=${moduleType}`,
      { headers }
    ).pipe(
      catchError(this.handleError)
    );
  }

  /**
   * Execute field API call
   * Backend handles placeholder resolution, API execution, rule evaluation, and field mapping
   * @param projectId Project ID
   * @param requestBody Request body containing fieldId, moduleType, and attribute values
   * @param uniqueKey The unique form key from the URL
   * @returns Observable with execution result
   */
  executeFieldApi(projectId: number, requestBody: ExecuteFieldApiRequest, uniqueKey: string): Observable<GeneralResponse<ExecuteFieldApiResponse>> {
    const headers = new HttpHeaders({
      'X-Form-Key': uniqueKey
    });

    return this.http.post<GeneralResponse<ExecuteFieldApiResponse>>(
      this.baseUrl + `/loyalty/api/public-form/execute-field-api?projectId=${projectId}`,
      requestBody,
      { headers }
    ).pipe(
      catchError(this.handleError)
    );
  }

}
